









































import {
  computed,
  defineComponent,
  PropType,
  ref,
  toRefs,
  useRouter
} from '~/utils/nuxt3-migration'
import { AuditActingUser, AuditRequest } from '~/models/audit/request'
import { ciAudit } from '~/icons/source/regular/audit'
import { ciReceipt } from '~/icons/source/regular/receipt'
import ClassifiedExternalCertificatesService from '~/services/classified/ClassifiedExternalCertificatesService'
import { useDep } from '~/compositions/dependency-container'
import { useFileUpload } from '~/compositions/files/upload'
import { useSnackbar } from '~/compositions/snackbar'
import { useLogger } from '~/compositions/logger'
import { useI18n } from '~/compositions/i18n'
import { handleError } from '~/utils/http'
import { ciUpload } from '~/icons/source/solid/upload'

export default defineComponent({
  props: {
    auditRequest: {
      type: Object as PropType<AuditRequest>,
      required: true
    },
    inModal: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  setup(props, { emit }) {
    const { auditRequest } = toRefs(props)
    const fileInputRef = ref(null)
    const uploadedReceiptUrl = ref<string | null>(null)
    const loading = ref<boolean>(false)

    const externalCertificatesService: ClassifiedExternalCertificatesService = useDep(
      ClassifiedExternalCertificatesService
    )

    const router = useRouter()
    const snackbar = useSnackbar()
    const logger = useLogger()
    const { t } = useI18n()
    const { filesAreOfAcceptableType } = useFileUpload()

    const userIsAuditProvider = computed<boolean>(() => {
      return (
        auditRequest.value.currentUserRole === AuditActingUser.AUDIT_PROVIDER
      )
    })

    const userIsPayer = computed<boolean>(() => {
      return auditRequest.value.currentUserRole === AuditActingUser.PAYER
    })

    const receiptLink = computed(() => {
      return auditRequest.value.receiptUrl || uploadedReceiptUrl.value
    })

    const to = computed(() => {
      if (userIsAuditProvider.value) {
        return {
          name: '__account_audits_external_certificates_view',
          params: { reportId: auditRequest.value.certificate.reportId }
        }
      }

      return {
        name: '__account_audits_requests_certificates_view',
        params: { certificateId: auditRequest.value.certificate.id }
      }
    })

    const href = computed(() => {
      return router.resolve(to.value).href
    })

    const triggerFileInput = () => {
      if (fileInputRef.value) {
        fileInputRef.value.value = null
        fileInputRef.value.click()
      }
    }

    const acceptedFileTypes = computed(() => [
      'image/jpeg',
      'image/png',
      'application/pdf'
    ])
    const fileInputChanged = async (e: Event) => {
      let currentFiles

      if (e.dataTransfer) {
        currentFiles = e.dataTransfer.files
      } else if (e.target) {
        currentFiles = e.target.files
      }

      if (currentFiles === null || !currentFiles.length) {
        return
      }

      if (
        acceptedFileTypes.value?.length &&
        !filesAreOfAcceptableType(currentFiles, acceptedFileTypes.value)
      ) {
        return alert(t('this file type is not supported'))
      }

      const fileToUpload = currentFiles[0]

      try {
        loading.value = true
        const {
          paymentReceipt
        } = await externalCertificatesService.uploadReceipt(
          fileToUpload,
          auditRequest.value.id
        )
        uploadedReceiptUrl.value = paymentReceipt
        emit('update-audit-request', {
          ...auditRequest.value,
          receiptUrl: paymentReceipt
        })
        snackbar.success(t('receipt_uploaded'))
      } catch (error) {
        if (fileInputRef.value) {
          fileInputRef.value.value = null
        }
        handleError(error, snackbar, logger, t)
      } finally {
        loading.value = false
      }
    }

    return {
      to,
      href,
      ciAudit,
      userIsAuditProvider,
      userIsPayer,
      ciReceipt,
      ciUpload,
      triggerFileInput,
      receiptLink,
      fileInputRef,
      fileInputChanged,
      uploadedReceiptUrl,
      acceptedFileTypes,
      loading
    }
  }
})
